import { ApplicationData } from "../ApplicationData";
import { FormatDate } from "../FormatDate";

const WEEKDAY_INDEX = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

function pad_number(value) {
    return String(value).padStart(2, "0");
}

function get_language() {
    let lang = "";

    try {
        lang = window?.language ?? "";
    } catch (error) {
        lang = "";
    }

    if (!lang && typeof ApplicationData !== "undefined") {
        try {
            lang = ApplicationData.get()?.user?.lang ?? "";
        } catch (error) {
            lang = "";
        }
    }

    if (!lang && typeof navigator !== "undefined") {
        lang = navigator.language ?? "";
    }

    return String(lang || "es").toLowerCase().startsWith("en") ? "en" : "es";
}

function get_zoned_parts(date, timeZone) {
    const parts = new Intl.DateTimeFormat("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        weekday: "short",
        hourCycle: "h23",
        timeZone: timeZone
    }).formatToParts(date);

    const values = {};
    for (const part of parts) {
        if (part.type !== "literal") {
            values[part.type] = part.value;
        }
    }

    const year = parseInt(values.year, 10);
    const month = parseInt(values.month, 10);
    const day = parseInt(values.day, 10);
    const hour = parseInt(values.hour, 10);
    const minute = parseInt(values.minute, 10);
    const second = parseInt(values.second, 10);

    return {
        year: year,
        month: month,
        day: day,
        hour: hour,
        minute: minute,
        second: second,
        weekday: WEEKDAY_INDEX[values.weekday],
        date_key: `${year}-${pad_number(month)}-${pad_number(day)}`,
        minutes: (hour * 60) + minute
    };
}

function get_minutes(time) {
    return (Number(time?.hour ?? 0) * 60) + Number(time?.minute ?? 0);
}

function is_day_in_range(day, start_day, end_day) {
    const start = Number(start_day);
    const end = Number(end_day);

    if (Number.isNaN(start) || Number.isNaN(end)) {
        return false;
    }

    if (start <= end) {
        return day >= start && day <= end;
    }

    return day >= start || day <= end;
}

function get_previous_day(day) {
    return (day + 6) % 7;
}

function is_within_time_window(current_minutes, start_minutes, end_minutes) {
    if (start_minutes < end_minutes) {
        return current_minutes >= start_minutes && current_minutes <= end_minutes;
    }

    if (start_minutes > end_minutes) {
        return current_minutes >= start_minutes || current_minutes <= end_minutes;
    }

    return current_minutes === start_minutes;
}

function get_time_zone_offset_minutes(date, timeZone) {
    const parts = get_zoned_parts(date, timeZone);
    const as_utc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
    const date_without_ms = Math.floor(date.getTime() / 1000) * 1000;

    return (as_utc - date_without_ms) / 60000;
}

function zoned_time_to_date(year, month, day, hour, minute, timeZone) {
    const local_as_utc = Date.UTC(year, month - 1, day, hour, minute, 0, 0);
    let timestamp = local_as_utc;

    for (let i = 0; i < 2; i++) {
        const offset = get_time_zone_offset_minutes(new Date(timestamp), timeZone);
        timestamp = local_as_utc - (offset * 60000);
    }

    return new Date(timestamp);
}

function format_display_time(date, lang, timeZone) {
    return new Intl.DateTimeFormat(lang === "es" ? "es-CO" : "en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: lang === "es",
        timeZone: timeZone
    }).format(date);
}

function get_exception_date_key(value, supportTimeZone) {
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
        return value;
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return get_zoned_parts(date, supportTimeZone).date_key;
}

function get_previous_date_key(reference, supportTimeZone) {
    const previous_date = zoned_time_to_date(
        reference.year,
        reference.month,
        reference.day - 1,
        12,
        0,
        supportTimeZone
    );

    return get_zoned_parts(previous_date, supportTimeZone).date_key;
}

/**
 * @typedef {object} TimeData
 * @property {number} hour - La hora (0-23).
 * @property {number} minute - El minuto (0-59).
 */

/**
 * @typedef {object} ExceptionData
 * @property {"date"|"weekday"} type - El tipo de excepción.
 * @property {string} [date] - La fecha de la excepción en formato ISO 8601.
 * @property {number} [weekday] - El día de la semana (0=Domingo, 6=Sábado).
 * @property {"inclusive"|"exclusive"} exception_type - Habilita o deshabilita el soporte.
 * @property {number} start_time_hour - La hora de inicio de la excepción (0-23).
 * @property {number} start_time_minute - El minuto de inicio de la excepción (0-59).
 * @property {number} end_time_hour - La hora de fin de la excepción (0-23).
 * @property {number} end_time_minute - El minuto de fin de la excepción (0-59).
 */

/**
 * @class support_customer_service
 * @description
 * Gestiona el horario de atención al cliente usando la zona horaria de soporte
 * como fuente de verdad. La evaluación de disponibilidad siempre se hace en
 * `supportTimeZone`; la zona del usuario solo se usa para mostrar el horario.
 */
class support_customer_service {

    /**
     * @param {object} [data={}] - Objeto de configuración del horario de soporte.
     * @param {string} [userTimeZone=Intl.DateTimeFormat().resolvedOptions().timeZone]
     * @param {function} [callback=function(){}]
     */
    constructor(data = {}, userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone, callback = function(){}) {
        this.start_day = data?.start_day ?? 1;
        this.end_day = data?.end_day ?? 5;
        this.start_time = {
            hour: data?.start_time?.hour ?? 9,
            minute: data?.start_time?.minute ?? 0
        };
        this.end_time = {
            hour: data?.end_time?.hour ?? 19,
            minute: data?.end_time?.minute ?? 30
        };
        this.exceptions = data?.exceptions ?? [];
        this.callback = callback ?? function(){};
        this.supportTimeZone = data?.supportTimeZone ?? "UTC";
        this.userTimeZone = userTimeZone;
    }

    /**
     * Construye los instantes absolutos de una ventana de soporte para una fecha
     * de referencia expresada en la zona horaria de soporte.
     *
     * @private
     * @param {{year:number, month:number, day:number}} reference
     * @param {TimeData} start_time
     * @param {TimeData} end_time
     * @returns {{start: Date, end: Date, start_user: object, end_user: object}}
     */
    get_support_window(reference, start_time, end_time) {
        const start_minutes = get_minutes(start_time);
        const end_minutes = get_minutes(end_time);
        const end_day_offset = end_minutes < start_minutes ? 1 : 0;

        const start = zoned_time_to_date(
            reference.year,
            reference.month,
            reference.day,
            start_time.hour,
            start_time.minute,
            this.supportTimeZone
        );

        const end = zoned_time_to_date(
            reference.year,
            reference.month,
            reference.day + end_day_offset,
            end_time.hour,
            end_time.minute,
            this.supportTimeZone
        );

        return {
            start: start,
            end: end,
            start_user: get_zoned_parts(start, this.userTimeZone),
            end_user: get_zoned_parts(end, this.userTimeZone)
        };
    }

    /**
     * Formatea una ventana de soporte en la zona horaria del usuario.
     *
     * @private
     * @param {string} day_label
     * @param {ReturnType<support_customer_service["get_support_window"]>} window
     * @param {"es"|"en"} lang
     * @returns {string}
     */
    format_support_window(day_label, window, lang) {
        const start_time = format_display_time(window.start, lang, this.userTimeZone);
        const end_time = format_display_time(window.end, lang, this.userTimeZone);
        const crosses_midnight = window.end_user.date_key !== window.start_user.date_key;
        const suffix = crosses_midnight
            ? (lang === "es" ? " Mañana" : " Tomorrow")
            : "";

        return `${day_label} ${start_time} ${lang === "es" ? "a" : "to"} ${end_time}${suffix}`;
    }

    /**
     * Determina si el soporte está deshabilitado en una fecha y hora dadas.
     * La comparación se hace directamente en `supportTimeZone`.
     *
     * @param {Date} [date=new Date()]
     * @returns {boolean}
     */
    isSupportButtonDisabled(date = new Date()) {
        const now = get_zoned_parts(date, this.supportTimeZone);
        const start_minutes = get_minutes(this.start_time);
        const end_minutes = get_minutes(this.end_time);
        const overnight = start_minutes > end_minutes;

        let enabled = is_day_in_range(now.weekday, this.start_day, this.end_day)
            && is_within_time_window(now.minutes, start_minutes, end_minutes);

        if (overnight && !enabled) {
            const previous_day_in_range = is_day_in_range(
                get_previous_day(now.weekday),
                this.start_day,
                this.end_day
            );

            if (previous_day_in_range && now.minutes <= end_minutes) {
                enabled = true;
            }
        }

        for (const exception of this.exceptions ?? []) {
            if (!exception) {
                continue;
            }

            const exception_start_minutes = (Number(exception.start_time_hour ?? 0) * 60)
                + Number(exception.start_time_minute ?? 0);
            const exception_end_minutes = (Number(exception.end_time_hour ?? 0) * 60)
                + Number(exception.end_time_minute ?? 0);
            const overnight_exception = exception_start_minutes > exception_end_minutes;

            let applies = false;
            let applies_to_previous_day = false;

            if (exception.type === "date") {
                const exception_date_key = get_exception_date_key(exception.date, this.supportTimeZone);
                applies = exception_date_key !== null && exception_date_key === now.date_key;

                if (!applies && overnight_exception && exception_date_key !== null) {
                    applies_to_previous_day = exception_date_key === get_previous_date_key(now, this.supportTimeZone);
                }
            } else if (exception.type === "weekday") {
                applies = Number(exception.weekday) === now.weekday;

                if (!applies && overnight_exception) {
                    applies_to_previous_day = Number(exception.weekday) === get_previous_day(now.weekday);
                }
            }

            if (!applies && !applies_to_previous_day) {
                continue;
            }

            const is_full_day_exception = (
                exception_start_minutes === 0
                && exception_end_minutes === (23 * 60) + 59
            );

            let within_exception = false;
            if (is_full_day_exception) {
                within_exception = true;
            } else if (applies && is_within_time_window(now.minutes, exception_start_minutes, exception_end_minutes)) {
                within_exception = true;
            } else if (applies_to_previous_day && now.minutes <= exception_end_minutes) {
                within_exception = true;
            }

            if (within_exception) {
                if (exception.exception_type === "exclusive") {
                    return true;
                }

                if (exception.exception_type === "inclusive") {
                    return false;
                }
            }
        }

        return !enabled;
    }

    /**
     * Genera un texto amigable con el horario de atención en la zona del usuario.
     *
     * @param {Date} [date=new Date()]
     * @returns {string}
     */
    isAvailableSupportText(date = new Date()) {
        const lang = get_language();
        const reference = get_zoned_parts(date, this.supportTimeZone);
        const days_es = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
        const days_en = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
        const days = lang === "es" ? days_es : days_en;

        const window = this.get_support_window(reference, this.start_time, this.end_time);
        let text = this.format_support_window(
            `${days[this.start_day]}-${days[this.end_day]}`,
            window,
            lang
        );

        const weekday_exceptions = (this.exceptions ?? []).filter((exception) => {
            return exception?.type === "weekday" && exception?.exception_type === "inclusive";
        });

        for (const exception of weekday_exceptions) {
            const exception_window = this.get_support_window(
                reference,
                {
                    hour: exception.start_time_hour ?? 0,
                    minute: exception.start_time_minute ?? 0
                },
                {
                    hour: exception.end_time_hour ?? 0,
                    minute: exception.end_time_minute ?? 0
                }
            );

            text += `,\n ${this.format_support_window(
                days[exception.weekday],
                exception_window,
                lang
            )}`;
        }

        return text;
    }
}

/**
 * @deprecated Usar `support_customer_service#isSupportButtonDisabled`.
 * Se conserva solo como compatibilidad para consumidores externos.
 *
 * @param {Date} [date=new Date()]
 * @returns {boolean}
 */
function isSupportButtonDisabled(date = new Date()) {
    const app = window?.support_customer_service_app ?? null;
    if (!app) {
        console.warn("[support_customer_service] Legacy isSupportButtonDisabled called before support data was loaded.");
        return true;
    }

    return app.isSupportButtonDisabled(date);
}

/**
 * @deprecated Usar `support_customer_service#isAvailableSupportText`.
 *
 * @returns {string}
 */
function isAvailableSupportText() {
    const app = window?.support_customer_service_app ?? null;
    return app ? app.isAvailableSupportText() : "";
}

let support_data_promise = null;

async function get_support_customer_service_data() {
    if (window.support_customer_service_data) {
        return window.support_customer_service_data;
    }

    if (support_data_promise !== null) {
        return support_data_promise;
    }

    let formatDate = new FormatDate(new Date());
    let monthday = formatDate.month_day_year_only_hours();

    try {
        if (window.Cookies.get("AppSessionSuperUser") !== null) {
            monthday = new Date().getTime();
        }
    } catch (error) {
        console.log(error);
    }

    const IS_ONLINE = window.IS_ONLINE;
    let bucket = "public-bucket-dev";
    if (IS_ONLINE === true) {
        bucket = "public-bucket";
    }

    support_data_promise = fetch(`https://${bucket}.3pod.io/tools/${IS_ONLINE ? "prod": "test"}/support_service_availability.json?time=${monthday}&v=1`)
        .then((response) => response.json())
        .then((data) => {
            window.support_customer_service_data = data;
            return data;
        })
        .finally(() => {
            support_data_promise = null;
        });

    return support_data_promise;
}

async function get_support_customer_service_app() {
    if (window.support_customer_service_app) {
        return window.support_customer_service_app;
    }

    const data = await get_support_customer_service_data();
    if (data === null || typeof data === "undefined") {
        throw new Error("support_customer_service_data are undefined or null");
    }

    window.support_customer_service_app = new support_customer_service(data);
    return window.support_customer_service_app;
}

class SupportDisabledBy 
{
    type = "";
    truthy = false;
    isButtonDisabled = true;
    support_app = null;
    support_customer_service_data = null;
    Contents = {};

    /**
     * 
     * @param {String} type Field name from support service fields. 
     * @param {Boolean} truthy do not depends on support_customer_service_data[type] 
     * @param {Boolean} isButtonDisabled 
     * @param {support_customer_service} support_app 
     * @param {Object} support_customer_service_data 
     */
    constructor({
        type = "any", 
        truthy = false, 
        isButtonDisabled = true, 
        support_app = null, 
        support_customer_service_data = {}
    }){
        this.type = type;
        this.truthy = truthy;
        this.isButtonDisabled = isButtonDisabled;
        this.support_customer_service_data = support_customer_service_data;
        /**
         * @type {support_customer_service}
         */
        this.support_app = support_app;
    }
    
    
    /**
     * 
     * @param {String} type Field name from support service fields. 
     * @param {Boolean} truthy do not depends on support_customer_service_data[type] 
     * @returns 
     */
    disabledByException(type, truthy = false){
        let active;
        try {
            active = this.support_customer_service_data[type];
            if(active === false){
                return true;
            }
        } catch (error) {
            console.error(error);
        }

        if(truthy === true){
            return false;
        }

        return this.isButtonDisabled;
    }

    disabledByExceptionText(type, show = true){
        if(show === false){
            return false;
        }

        try {
            if(this.support_customer_service_data[type] === false){
                return this.Contents?.ctn_PLUxD2 ?? "No disponible por el momento.";
            }
        } catch (error) {
            console.error(error);
        }

        return this.support_app.isAvailableSupportText();
    }
}

window.isSupportButtonDisabled = isSupportButtonDisabled;

export {
    get_support_customer_service_data,
    get_support_customer_service_app,
    isSupportButtonDisabled,
    isAvailableSupportText,
    support_customer_service,
    SupportDisabledBy
}
